import { useCallback, useEffect, useRef } from 'react';

import { useUpdateOnlineStatus } from './useUpdateOnlineStatus';

interface UseOnlineStatusTrackerOptions {
  /**
   * Interval in milliseconds for periodic updates
   * Default: 5 minutes (300000ms)
   */
  interval?: number;
  /**
   * Whether to track user activity (mouse, keyboard, page visibility)
   * Default: true
   */
  trackActivity?: boolean;
  /**
   * Minimum time between activity-triggered updates in milliseconds
   * Default: 1 minute (60000ms)
   */
  activityThrottle?: number;
}

export const useOnlineStatusTracker = ({
  interval = 5 * 60 * 1000, // 5 minutes
  trackActivity = true,
  activityThrottle = 60 * 1000, // 1 minute
}: UseOnlineStatusTrackerOptions = {}) => {
  const { mutate: updateOnlineStatus } = useUpdateOnlineStatus();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityUpdateRef = useRef<number>(0);

  // Handle activity-based updates (throttled)
  const handleActivity = useCallback(() => {
    if (!trackActivity) return;

    const now = Date.now();
    const timeSinceLastUpdate = now - lastActivityUpdateRef.current;

    if (timeSinceLastUpdate >= activityThrottle) {
      updateOnlineStatus();
      lastActivityUpdateRef.current = now;
    }
  }, [updateOnlineStatus, trackActivity, activityThrottle]);

  // Handle page visibility change
  const handleVisibilityChange = useCallback(() => {
    if (document.visibilityState === 'visible') {
      // User returned to the tab, update online status
      updateOnlineStatus();
      lastActivityUpdateRef.current = Date.now();
    }
  }, [updateOnlineStatus]);

  // Start periodic tracking
  const startTracking = useCallback(() => {
    // Initial update
    updateOnlineStatus();
    lastActivityUpdateRef.current = Date.now();

    // Set up periodic updates
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      updateOnlineStatus();
    }, interval);

    // Set up activity listeners
    if (trackActivity) {
      // Mouse and keyboard activity
      window.addEventListener('mousemove', handleActivity, { passive: true });
      window.addEventListener('keydown', handleActivity, { passive: true });
      window.addEventListener('click', handleActivity, { passive: true });
      window.addEventListener('scroll', handleActivity, { passive: true });
    }

    // Page visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);
  }, [
    interval,
    trackActivity,
    handleActivity,
    handleVisibilityChange,
    updateOnlineStatus,
  ]);

  // Stop tracking
  const stopTracking = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (trackActivity) {
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('click', handleActivity);
      window.removeEventListener('scroll', handleActivity);
    }

    document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [trackActivity, handleActivity, handleVisibilityChange]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopTracking();
    };
  }, [stopTracking]);

  return {
    startTracking,
    stopTracking,
    updateOnlineStatusNow: updateOnlineStatus,
  };
};
